import { useQuery } from "@tanstack/react-query";
import { planQuery } from "../../lib/queries";
import { Button, QuietButton } from "../ui/button";
import { UPGRADE_LEAD_FREE_DOORMAN, UpgradeOffer } from "../plan/upgrade-offer";
import { SEE_THE_PLANS, START_TRIAL, trialOfferLine } from "../plan/trial-gate";
import { NobFace } from "./mascot";
import { Sheet } from "./surfaces";
import { useShellUi } from "./state";

/**
 * THE TITLE, ON A TIER THAT DOES NO AI WORK (ruling C10, PRD §1.5).
 *
 * ONE OPENER, AND IT IS FREE'S. The paywall used to answer two refusals: a
 * doorman asked for on Free, and a knock start refused at the send, which
 * opened this sheet by itself on the first `PLAN_EXHAUSTED`. The second is
 * gone (owner, 2026-09-23; wave 61): "Button is better, keep it uniform." A
 * refused knock start now shows the same one line and one control as every
 * other conversation (`SpentRow`), and nothing opens by itself. What is left
 * is `rules-panel.tsx`'s "Tell Nob" on Free (`metered === false`), so this
 * sheet speaks only to somebody with no allowance: no period to have used and
 * no top rung.
 */
export const PAYWALL_FREE_DOORMAN_TITLE = "Your doorman takes a plan.";

export const PAYWALL_PLANS = SEE_THE_PLANS;
export const PAYWALL_DISMISS = "Not now";

/**
 * The §5.4 offer: the next rung, priced, and one button to the plans.
 *
 * NO FIGURE FOR WHAT WAS REFUSED. The dialog names what a plan costs, because
 * somebody buying one has to see what they are buying (§6.4). It never names
 * the price of the action that hit this wall — that is a per-action price at
 * the moment of use, which is the model this product retired (PRD v3.3 §1.5a).
 */
export function PaywallDialog() {
  const ui = useShellUi();
  const { data: plan } = useQuery(planQuery());
  const nextTier = plan?.plan.next_tier ?? null;
  /** The trial, when `GET /plan` reports one (ruling R-1006-2): it leads, the plans follow. */
  const trial = plan?.trial ?? null;

  return (
    <Sheet title={PAYWALL_FREE_DOORMAN_TITLE} onClose={ui.closePaywall}>
      <div className="flex justify-center pb-2">
        <NobFace mood="doubting" size="xl" />
      </div>

      {nextTier && plan ? (
        /* NO `onSeePlans` HERE, and that is the point: the button below is
           this screen's one call to action, and a second one inside the card
           was a duplicate that reloaded the whole bundle to reach the pane
           this dialog opens in place. */
        <UpgradeOffer
          nextTier={nextTier}
          ladder={plan.ladder}
          lead={UPGRADE_LEAD_FREE_DOORMAN}
          priceSuffix={plan.tax.suffix}
        />
      ) : null}
      {trial ? (
        <>
          {/* THE PRIMARY ACTION IS THE TRIAL while one is on offer, and it only
              opens the plan room at the trial's consent — the consent stays
              there. The offer, on one line, in the trial screen's own terms. */}
          <Button
            type="button"
            className="mt-4 h-12 w-full"
            data-trial-action
            onClick={() => {
              ui.closePaywall();
              ui.openBilling("plans", "trial");
            }}
          >
            {START_TRIAL}
          </Button>
          <p className="mt-1.5 text-center text-xs text-muted" data-trial-line>
            {trialOfferLine(trial)}
          </p>
          <QuietButton
            type="button"
            className="mt-1 h-11 w-full"
            onClick={() => {
              ui.closePaywall();
              ui.openBilling("plans");
            }}
          >
            {PAYWALL_PLANS}
          </QuietButton>
        </>
      ) : (
        <Button
          type="button"
          className="mt-4 h-12 w-full"
          onClick={() => {
            ui.closePaywall();
            ui.openBilling("plans");
          }}
        >
          {PAYWALL_PLANS}
        </Button>
      )}

      <QuietButton type="button" className="mt-1 h-11 w-full" onClick={ui.closePaywall}>
        {PAYWALL_DISMISS}
      </QuietButton>
    </Sheet>
  );
}
